// Builds a concrete "plan" of rounds for a single lesson, given the level
// and the active user's weak / slow keys.
//
// Most of the per-character bias logic lives inside data/levels.ts; this
// module is about (a) restricting weak/slow keys to letters the level has
// actually unlocked (don't drill keys the user hasn't been taught) and
// (b) instantiating each RoundTemplate into a concrete target text.

import type { RoundPresentation, RoundTemplate, Trin, UserProfile } from '../types';
import {
  effectiveMetronomeBpmForUser,
  shadowCpsForUser,
} from './presentationTempo';
import { effectiveNiveau4Lexicon } from './niveau4Lexicon';
import { FLOW_PRACTICE_TRIN_ID } from './flowPractice';
import { slowKeysMap } from './stats';

/** A concrete round, ready to play. */
export interface PreparedRound {
  template: RoundTemplate;
  targetText: string;
  index: number;
  total: number;
}

export interface PrepareLessonOptions {
  /** Override the user's weak-key data (mostly for tests). */
  weakKeysOverride?: Record<string, number>;
  /** Override the user's slow-key data (mostly for tests). */
  slowKeysOverride?: Record<string, number>;
  /** Optional seed for deterministic output. */
  seed?: number;
}

/**
 * Generate the ordered list of rounds for a lesson.
 *
 *   1. Restrict the user's `errorsByKey` and slow-key map to letters the
 *      level has actually unlocked, so the generators only bias toward
 *      keys the user has actually been taught.
 *   2. Walk the level's `rounds` in order and instantiate each generator.
 *   3. The MVP keeps the round sequence fixed — the level author has
 *      already chosen a good guided -> recall -> challenge ramp. Adaptive
 *      logic affects WHICH characters appear inside each generated text,
 *      not the order of rounds.
 *
 * The output is a `PreparedRound[]` so the lesson screen knows how many
 * rounds there are and which index it's on. Each round picks up the
 * user's latest weak/slow data from the lesson's *starting* snapshot —
 * within a lesson we don't shift bias mid-flight.
 */
/** Index of the first dictation round in a trin, or -1 if none. */
export function firstDictationRoundIndex(trin: Trin): number {
  return trin.rounds.findIndex((r) => r.presentation?.kind === 'dictation');
}

export function prepareLesson(
  trin: Trin,
  user: UserProfile,
  opts: PrepareLessonOptions = {},
): PreparedRound[] {
  const allowed = new Set(trin.unlockedKeys);

  const weak = filterMap(
    opts.weakKeysOverride ?? user.errorsByKey,
    allowed,
  );
  const slow = filterMap(
    opts.slowKeysOverride ?? slowKeysMap(user.responseTimeAvgs),
    allowed,
  );

  const total = trin.rounds.length;
  const niveau4Lexicon =
    trin.tierId === 'niveau-4' ? effectiveNiveau4Lexicon(user) : undefined;
  const flowLexicon =
    trin.id === FLOW_PRACTICE_TRIN_ID
      ? user.preferences?.flowLexicon === 'en'
        ? 'en'
        : 'da'
      : undefined;
  return trin.rounds.map((template, i) => {
    const resolved = applyPresentationTempo(template, user);
    return {
      template: resolved,
      index: i,
      total,
      targetText: resolved.generate({
        weakKeys: weak,
        slowKeys: slow,
        seed: opts.seed === undefined ? undefined : opts.seed + i,
        ...(niveau4Lexicon ? { niveau4Lexicon } : {}),
        ...(flowLexicon ? { flowLexicon } : {}),
      }),
    };
  });
}

function applyPresentationTempo(
  template: RoundTemplate,
  user: UserProfile,
): RoundTemplate {
  const p = template.presentation;
  if (!p) return template;

  let presentation: RoundPresentation = p;
  if (p.kind === 'metronome') {
    presentation = {
      kind: 'metronome',
      bpm: effectiveMetronomeBpmForUser(user),
    };
  } else if (p.kind === 'shadow') {
    presentation = { kind: 'shadow', cps: shadowCpsForUser(user) };
  }

  if (presentation === p) return template;
  return { ...template, presentation };
}

function filterMap(
  map: Record<string, number>,
  allowed: Set<string>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(map)) {
    if (allowed.has(k)) out[k] = v;
  }
  return out;
}
